<template>
  <footer v-if="base" class="legal-links" :aria-label="t('legal.label')">
    <nav class="legal-links__nav">
      <a class="legal-links__link" :href="`${base}/imprint`" target="_blank" rel="noopener">{{ t('legal.imprint') }}</a>
      <span class="legal-links__sep" aria-hidden="true">·</span>
      <a class="legal-links__link" :href="`${base}/privacy`" target="_blank" rel="noopener">{{ t('legal.privacy') }}</a>
      <span class="legal-links__sep" aria-hidden="true">·</span>
      <!-- Third link (issue 332): same landingBaseUrl-derived pattern as the
           two above, no new prop/config needed. -->
      <a class="legal-links__link" :href="`${base}/terms`" target="_blank" rel="noopener">{{ t('legal.terms') }}</a>
      <!-- Fourth link (issue 576), OPT-IN: the active tenant's own employee
           privacy & AI notice, alongside Lumi's own imprint/privacy above.
           Caller-supplied because app-kit owns no runtime config and no
           tenant context (ADR 0026) — a UI that knows neither the workspace
           base URL nor the tenant slug passes nothing, and nothing renders,
           the same "no base → no link" rule `base` itself follows. -->
      <template v-if="tenantNoticeUrl">
        <span class="legal-links__sep" aria-hidden="true">·</span>
        <a class="legal-links__link" :href="tenantNoticeUrl" target="_blank" rel="noopener">
          {{ t('legal.employeeNotice', { company: tenantNoticeCompany || '' }) }}
        </a>
      </template>
    </nav>
  </footer>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

// The suite's shared legal footer (issue 221). Lumi owes a § 5 DDG imprint on
// every public-facing surface, and the answer is ONE place: the marketing site
// carries the imprint and the privacy notice, and every app links to it from
// here. Five per-app copies would drift the moment one of the facts changes.
//
// `landingBaseUrl` is injected rather than derived here for the reason
// docs/operations/environments.md gives: no bare `lumi-hr.com` literal may exist in source —
// every host is derived from the runtime LUMI_DOMAIN, and that resolution lives
// in each app's own runtime-config (app-kit owns no runtime config). An
// unresolvable base renders NOTHING rather than a link into the void: a footer
// that 404s is worse than no footer, and the caller's resolver has a dev
// fallback, so this only bites where there is genuinely no domain to point at.
const props = defineProps<{
  landingBaseUrl: string;
  /** Full URL to the active tenant's public employee-notice page (issue 576). Omit to render no fourth link. */
  tenantNoticeUrl?: string;
  /** The tenant's display name, interpolated into `legal.employeeNotice`. */
  tenantNoticeCompany?: string;
}>();

const { t } = useI18n();
const base = computed(() => String(props.landingBaseUrl || '').replace(/\/+$/, ''));
</script>

<style scoped>
.legal-links {
  display: flex;
  justify-content: center;
  padding: 1.25rem 1rem 1.75rem;
}
.legal-links__nav {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: var(--text-sm);
  color: var(--text-muted);
}
.legal-links__link {
  color: inherit;
  text-decoration: none;
}
.legal-links__link:hover {
  color: var(--text-heading);
  text-decoration: underline;
}
.legal-links__sep {
  opacity: 0.5;
}
</style>
